import { createRoute, lazyRouteComponent, redirect } from "@tanstack/react-router";
import { approvalStatusSchema, gigStatusSchema } from "api/resources/shared";
import { PARTICIPANT_STATES } from "domains/admin/components/TaskOpsParticipantsTable/helpers";
import { queries as applicationHistoryQueries } from "domains/admin/containers/ApplicationHistory/queries";
import { applicationHistorySearchSchema } from "domains/admin/containers/ApplicationHistory/routing";
import { queries as proNotesTableQueries } from "domains/admin/containers/ProNotesTable/queries";
import { queries as proPerformanceQueries } from "domains/admin/containers/ProPerformance/queries";
import { queries as proSuspensionsQueries } from "domains/admin/containers/ProSuspensions/queries";
import { queries as taskOpsInterestedUsersQueries } from "domains/admin/containers/TaskOpsInterestedUsers/queries";
import { queries as adminAlertsQueries } from "domains/admin/views/AdminAlerts/queries";
import { queries as adminGigsQueries } from "domains/admin/views/AdminGigs/queries";
import { queries as adminJobsQueries } from "domains/admin/views/AdminJobs/queries";
import { queries as adminLocationsQueries } from "domains/admin/views/AdminLocations/queries";
import { queries as adminProsQueries } from "domains/admin/views/AdminPros/queries";
import { queries as adminUsersQueries } from "domains/admin/views/AdminUsers/queries";
import { queries as atRiskProsQueries } from "domains/admin/views/AtRiskPros/queries";
import { queries as businessCouponsQueries } from "domains/admin/views/BusinessCoupons/queries";
import { queries as certificationsQueries } from "domains/admin/views/CertificationManagement/queries";
import { queries as commandCenterQueries } from "domains/admin/views/CommandCenter/queries";
import { queries as configurationQueries } from "domains/admin/views/Configuration/queries";
import { queries as dynamicLinksQueries } from "domains/admin/views/DynamicLinks/queries";
import { queries as invoicingBackfillDetailQueries } from "domains/admin/views/InvoicingBackfillDetail/queries";
import { queries as invoicingBackfillsQueries } from "domains/admin/views/InvoicingBackfills/queries";
import { queries as invoicingRunDetailQueries } from "domains/admin/views/InvoicingDraftDetail/queries";
import { queries as invoicingRunsQueries } from "domains/admin/views/InvoicingDrafts/queries";
import { RUN_TYPE_FILTERS } from "domains/admin/views/InvoicingDrafts/RunTypeChip";
import { RUN_STATUS_FILTERS } from "domains/admin/views/InvoicingDrafts/statusFilter";
import { ManageProLayout } from "domains/admin/views/ManagePros";
import { HighDemand } from "domains/admin/views/ManagePros/HighDemand";
import { Overview } from "domains/admin/views/ManagePros/Overview";
import { ProNotes } from "domains/admin/views/ManagePros/ProNotes";
import {
  blocksQueries,
  highDemandQuery,
  jobApplicationsQueries,
  overviewQueries,
  queries as manageProQueries,
  sickTimeQueries,
} from "domains/admin/views/ManagePros/queries";
import { SickTime } from "domains/admin/views/ManagePros/SickTime";
import { queries as referralCodeQueries } from "domains/admin/views/ReferralCodeList/queries";
import { queries as referralPromotionsQueries } from "domains/admin/views/ReferralPromotions/queries";
import { TaskOpsLayout } from "domains/admin/views/TaskOps";
import { queries as taskOpsParticipantsQueries } from "domains/admin/views/TaskOps/queries";
import { requireAdmin } from "domains/auth/access/admin";
import { gigSidebarSchema } from "domains/gigs/containers/GigInfoSidebar/routing";
import { gigFilterSchema } from "domains/schedule/containers/Filters/routing";
import { queries as permissionsQueries } from "layouts/SuperAdminPanel/AdminPermissions/queries";
import { getFeatureFlag } from "providers/LaunchDarkly/client";
import { warmQueries } from "routing/warmQueries";
import * as z from "zod/mini";
import { signedUpRoute } from "./signedUp";

const prosSearch = z.object({
  page: z._default(z.number(), 1),
  page_size: z._default(z.number(), 25),
  ordering: z._default(z.string(), "user_id"),
  name: z.optional(z.string()),
  phone_number: z.optional(z.string()),
  user_id: z.optional(z.number()),
  verified: z.optional(z.boolean()),
});

const jobApplicationsSearch = z.object({
  page: z._default(z.number(), 1),
  page_size: z._default(z.number(), 50),
});

const adminAlertsSearch = z.object({
  alert_level: z.optional(z.enum(["CRITICAL", "WARNING", "ON_TIME"])),
  gig_id: z.optional(z.number()),
  page: z._default(z.number(), 1),
  page_size: z._default(z.number(), 25),
});

const commandCenterSearch = z.object({
  status: z.optional(z.enum(["all", "at_risk", "unsolved"])),
  location_id: z.optional(z.number()),
  market_id: z._default(z.array(z.number()), []),
  skill_id: z._default(z.array(z.number()), []),
  gig_type: z.optional(z.string()),
  auto_select: z.optional(z.boolean()),
  upcoming_days: z.optional(z.number()),
  search: z.optional(z.string()),
  gig_id: z.optional(z.number()),
  page: z._default(z.number(), 1),
  fill_status: z.optional(z.enum(["unfilled", "filled", "at_risk", "has_at_risk"])),
  needs_attention: z.optional(z.boolean()),
  is_vip: z.optional(z.boolean()),
  is_first_5_for_location: z.optional(z.boolean()),
  c_source: z.optional(z.string()),
  c_type: z.optional(z.string()),
  c_status: z.optional(z.string()),
  c_ordering: z.optional(z.string()),
  c_min_score: z.optional(z.number()),
  c_max_distance: z.optional(z.number()),
  c_min_rating: z.optional(z.number()),
  c_min_paid_gigs: z.optional(z.number()),
  c_min_cross_coef: z.optional(z.number()),
  c_has_push_token: z.optional(z.boolean()),
  c_at_risk: z.optional(z.boolean()),
});

const atRiskProsSearch = z.object({
  at_risk: z.optional(z.boolean()),
  risk_cohort: z.optional(z.enum(["cancellation", "inexperienced", "dormant", "expiring"])),
  market_id: z._default(z.array(z.number()), []),
  location_id: z.optional(z.number()),
  skill_id: z.optional(z.number()),
  auto_select: z.optional(z.boolean()),
  upcoming_days: z.optional(z.number()),
  is_vip: z.optional(z.boolean()),
  is_first_5_for_location: z.optional(z.boolean()),
  first_confirm: z.optional(z.boolean()),
  outreach_status: z.optional(z.enum(["no_outreach", "outreach_successful", "outreach_attempted_no_response"])),
  search: z.optional(z.string()),
  sort: z._default(z.enum(["shift_asc", "shift_desc", "name", "market"]), "shift_asc"),
});

const gigsSearch = z.object({
  ...gigFilterSchema.shape,
  ...gigSidebarSchema.shape,
  ...applicationHistorySearchSchema,
  page: z._default(z.number(), 1),
  page_size: z._default(z.number(), 50),
  ordering: z._default(z.string(), "-id"),
  created_ts__gte: z.optional(z.coerce.date()),
  created_ts__lte: z.optional(z.coerce.date()),
  status: z.optional(gigStatusSchema),
  skill_id: z.optional(z.number()),
  location_id: z.optional(z.number()),
  event_id: z.optional(z.string()),
  expanded_gig_ids: z._default(z.array(z.number()), []),
});

const jobsSearch = z.object({
  page: z._default(z.number(), 1),
  page_size: z._default(z.number(), 50),
  ordering: z._default(z.string(), "-created_ts"),
  search: z.optional(z.string()),
  status: z.optional(z.enum(["pending", "open", "filled", "archived"])),
  location_id: z.optional(z.number()),
  location_name: z.optional(z.string()),
  location__market_id: z.optional(z.number()),
  market_id: z.optional(z.number()),
  market_name: z.optional(z.string()),
  name: z.optional(z.string()),
  organization_id: z.optional(z.string()),
  organization_name: z.optional(z.string()),
  position_id: z.optional(z.number()),
  job_type_id: z.optional(z.number()),
  created_by_crawler: z.optional(z.boolean()),
  origin: z.optional(z.enum(["posted", "direct_hire"])),
  is_private: z.optional(z.boolean()),
  expanded_job_ids: z._default(z.array(z.string()), []),
});

const locationsSearch = z.object({
  page: z._default(z.number(), 1),
  page_size: z._default(z.number(), 25),
  ordering: z._default(z.string(), "-created_ts"),
  id: z.optional(z.number()),
  name: z.optional(z.string()),
  organization_name: z.optional(z.string()),
  market_id: z.optional(z.number()),
  approval_status: z.optional(approvalStatusSchema),
  creator_firstname: z.optional(z.string()),
  creator_lastname: z.optional(z.string()),
});

const usersSearch = z.object({
  page: z._default(z.number(), 1),
  page_size: z._default(z.number(), 25),
  ordering: z._default(z.string(), "-id"),
  id: z.optional(z.number()),
  uuid: z.optional(z.string()),
  gigster_profile__id: z.optional(z.number()),
  first_name: z.optional(z.string()),
  last_name: z.optional(z.string()),
  preferred_name_for_search: z.optional(z.string()),
  combined_search_param: z.optional(z.string()),
  email: z.optional(z.string()),
  phone_number: z.optional(z.string()),
  app_mode: z.optional(z.string()),
  account_status: z.optional(z.string()),
  date_joined: z.optional(z.coerce.date()),
  last_login: z.optional(z.coerce.date()),
});

const certificationsSearch = z.object({
  page: z._default(z.number(), 1),
  page_size: z._default(z.number(), 50),
  ordering: z._default(z.string(), "-id"),
  search: z.optional(z.string()),
  status: z.optional(z.enum(["PENDING", "APPROVED", "REJECTED", "EXPIRED"])),
  certification: z.optional(z.number()),
  market_id: z.optional(z.number()),
  gigster__market__name: z.optional(z.string()),
  created_by__preferred_name_for_search: z.optional(z.string()),
});

/** Exported so a story can wrap it in `resilientValidateSearch` the way
 * `hardenSearchValidation` does in production — reading it back off
 * `invoicingRuns.options.validateSearch` yields TanStack's `Constrain<...>`
 * wrapper, which is the same object but no longer a `z.ZodType`. */
export const invoicingRunsSearch = z.object({
  ordering: z.optional(z.string()),
  billing_source: z.optional(z.enum(["ORGANIZATION", "LOCATION"])),
  invoice_style: z.optional(z.enum(["STANDARD", "LOCATION", "ADDRESS"])),
  type: z.optional(z.enum(RUN_TYPE_FILTERS)),
  status: z.optional(z.enum(RUN_STATUS_FILTERS)),
  period_end__gte: z.optional(z.coerce.date()),
  period_end__lte: z.optional(z.coerce.date()),
  search: z.optional(z.string()),
  id: z.optional(z.number()),
});

const listPageSearch = z.object({
  page: z._default(z.number(), 1),
  page_size: z._default(z.number(), 50),
});

const dynamicLinksSearch = z.object({
  page: z._default(z.number(), 1),
  page_size: z._default(z.number(), 25),
  path: z.optional(z.string()),
});

const taskOpsInterestedUsersSearch = z.object({
  page: z._default(z.number(), 1),
});

const taskOpsParticipantsSearch = z.object({
  page: z._default(z.number(), 1),
  page_size: z._default(z.number(), 25),
  search: z.optional(z.string()),
  market: z.optional(z.number()),
  state: z.optional(z.array(z.enum(PARTICIPANT_STATES))),
});

// ManagePros/TaskOps hid a react-router `<Routes>` switch behind one lazy import; those
// switches become real TanStack children below, so Overview/ProNotes (read a path param as
// a prop) are statically imported and wrapped — a route `component` gets no props from TanStack.

const adminRoot = createRoute({
  getParentRoute: () => signedUpRoute,
  path: "admin",
  beforeLoad: ({ context }) => requireAdmin(context.auth.user),
});

const index = createRoute({
  getParentRoute: () => adminRoot,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/admin/locations" });
  },
});

export const pros = createRoute({
  getParentRoute: () => adminRoot,
  path: "pros",
  validateSearch: prosSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, adminProsQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/AdminPros"), "Component"),
});

export const prosDetail = createRoute({
  getParentRoute: () => adminRoot,
  path: "pros/$id",
  loader: ({ params, context }) => warmQueries(context.queryClient, manageProQueries({ proId: Number(params.id) })),
  component: function ManagePro() {
    const { id } = prosDetail.useParams();
    return <ManageProLayout proId={Number(id)} />;
  },
});

const prosDetailIndex = createRoute({
  getParentRoute: () => prosDetail,
  path: "/",
  loader: ({ params, context }) => warmQueries(context.queryClient, overviewQueries({ proId: Number(params.id) })),
  component: function ProsOverview() {
    const { id } = prosDetail.useParams();
    return <Overview proId={Number(id)} />;
  },
});

const prosApplicationHistory = createRoute({
  getParentRoute: () => prosDetail,
  path: "application-history",
  validateSearch: z.object(applicationHistorySearchSchema),
  loaderDeps: ({ search }) => search,
  loader: ({ params, deps, context }) =>
    warmQueries(context.queryClient, applicationHistoryQueries({ proId: Number(params.id), search: deps })),
  component: lazyRouteComponent(
    () => import("domains/admin/views/ManagePros/ApplicationHistoryPage"),
    "ApplicationHistoryPage",
  ),
});

export const prosJobApplications = createRoute({
  getParentRoute: () => prosDetail,
  path: "job-applications",
  validateSearch: jobApplicationsSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ params, deps, context }) =>
    warmQueries(context.queryClient, jobApplicationsQueries({ proId: Number(params.id), search: deps })),
  component: lazyRouteComponent(
    () => import("domains/admin/views/ManagePros/JobApplicationsPage"),
    "JobApplicationsPage",
  ),
});

const prosPerformance = createRoute({
  getParentRoute: () => prosDetail,
  path: "performance",
  loader: ({ params, context }) =>
    warmQueries(context.queryClient, proPerformanceQueries({ proId: Number(params.id) })),
  component: lazyRouteComponent(() => import("domains/admin/containers/ProPerformance"), "ProPerformance"),
});

const prosSuspensions = createRoute({
  getParentRoute: () => prosDetail,
  path: "suspensions",
  loader: ({ params, context }) =>
    warmQueries(context.queryClient, proSuspensionsQueries({ proId: Number(params.id) })),
  component: lazyRouteComponent(() => import("domains/admin/containers/ProSuspensions"), "ProSuspensions"),
});

export const prosBlocks = createRoute({
  getParentRoute: () => prosDetail,
  path: "blocks",
  loader: ({ params, context }) => warmQueries(context.queryClient, blocksQueries({ proId: Number(params.id) })),
  validateSearch: z.object({ locationParams: z.optional(locationsSearch) }),
  component: lazyRouteComponent(() => import("domains/admin/views/ManagePros/Blocks"), "Blocks"),
});

const prosHighDemand = createRoute({
  getParentRoute: () => prosDetail,
  path: "high-demand",
  loader: ({ params, context }) => warmQueries(context.queryClient, [highDemandQuery({ proId: Number(params.id) })]),
  component: function ProsHighDemand() {
    const { id } = prosDetail.useParams();
    return <HighDemand proId={Number(id)} />;
  },
});

const prosNotes = createRoute({
  getParentRoute: () => prosDetail,
  path: "notes",
  loader: ({ params, context }) => warmQueries(context.queryClient, proNotesTableQueries({ proId: Number(params.id) })),
  component: function ProsNotes() {
    const { id } = prosDetail.useParams();
    return <ProNotes proId={Number(id)} />;
  },
});

const prosSickTime = createRoute({
  getParentRoute: () => prosDetail,
  path: "sick-time",
  beforeLoad: async ({ params, context }) => {
    const enabled = await getFeatureFlag("pro-sick-time", { queryClient: context.queryClient });
    if (!enabled) throw redirect({ to: "/admin/pros/$id", params });
  },
  loader: ({ params, context }) => warmQueries(context.queryClient, sickTimeQueries({ proId: Number(params.id) })),
  component: function ProsSickTime() {
    const { id } = prosDetail.useParams();
    return <SickTime proId={Number(id)} />;
  },
});

export const gigs = createRoute({
  getParentRoute: () => adminRoot,
  path: "gigs",
  validateSearch: gigsSearch,
  loaderDeps: ({ search: { expanded_gig_ids: _, applicationParams: __, ...search } }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, adminGigsQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/AdminGigs"), "Component"),
});

export const jobs = createRoute({
  getParentRoute: () => adminRoot,
  path: "jobs",
  validateSearch: jobsSearch,
  loaderDeps: ({ search: { expanded_job_ids: _, ...search } }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, adminJobsQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/AdminJobs"), "Component"),
});

export const users = createRoute({
  getParentRoute: () => adminRoot,
  path: "users",
  validateSearch: usersSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, adminUsersQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/AdminUsers"), "Component"),
});

export const certifications = createRoute({
  getParentRoute: () => adminRoot,
  path: "certifications",
  validateSearch: certificationsSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, certificationsQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/CertificationManagement"), "Component"),
});

export const locations = createRoute({
  getParentRoute: () => adminRoot,
  path: "locations",
  validateSearch: locationsSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, adminLocationsQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/AdminLocations"), "Component"),
});

export const commandCenter = createRoute({
  getParentRoute: () => adminRoot,
  path: "command-center",
  validateSearch: commandCenterSearch,
  loaderDeps: ({ search: { c_at_risk: _, ...search } }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, commandCenterQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/CommandCenter"), "Component"),
});

export const atRiskPros = createRoute({
  getParentRoute: () => adminRoot,
  path: "at-risk-pros",
  validateSearch: atRiskProsSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, atRiskProsQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/AtRiskPros"), "Component"),
});

export const businessCoupons = createRoute({
  getParentRoute: () => adminRoot,
  path: "business-coupons",
  validateSearch: listPageSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, businessCouponsQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/BusinessCoupons"), "Component"),
});

export const dynamicLinks = createRoute({
  getParentRoute: () => adminRoot,
  path: "dynamic-links",
  validateSearch: dynamicLinksSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, dynamicLinksQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/DynamicLinks"), "Component"),
});

export const referralCode = createRoute({
  getParentRoute: () => adminRoot,
  path: "referral-code",
  validateSearch: listPageSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, referralCodeQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/ReferralCodeList"), "Component"),
});

const referralPromotions = createRoute({
  getParentRoute: () => adminRoot,
  path: "referral-promotions",
  loader: ({ context }) => warmQueries(context.queryClient, referralPromotionsQueries()),
  component: lazyRouteComponent(() => import("domains/admin/views/ReferralPromotions"), "Component"),
});

export const configuration = createRoute({
  getParentRoute: () => adminRoot,
  path: "configuration",
  validateSearch: listPageSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, configurationQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/Configuration"), "Component"),
});

const permissions = createRoute({
  getParentRoute: () => adminRoot,
  path: "permissions",
  loader: ({ context }) => warmQueries(context.queryClient, permissionsQueries({ user: context.auth.user })),
  component: lazyRouteComponent(() => import("layouts/SuperAdminPanel/AdminPermissions"), "Component"),
});

export const adminAlerts = createRoute({
  getParentRoute: () => adminRoot,
  path: "admin-alerts",
  validateSearch: adminAlertsSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, adminAlertsQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/AdminAlerts"), "Component"),
});

const taskOps = createRoute({
  getParentRoute: () => adminRoot,
  path: "task-ops",
  beforeLoad: async ({ context }) => {
    const allowed = await getFeatureFlag("task-upload-allowed", { queryClient: context.queryClient });
    if (!allowed) throw redirect({ to: "/admin" });
  },
  component: TaskOpsLayout,
});

const taskOpsIndex = createRoute({
  getParentRoute: () => taskOps,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/admin/task-ops/interested-users" });
  },
});

export const taskOpsInterestedUsers = createRoute({
  getParentRoute: () => taskOps,
  path: "interested-users",
  validateSearch: taskOpsInterestedUsersSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, taskOpsInterestedUsersQueries({ search: deps })),
  component: lazyRouteComponent(
    () => import("domains/admin/containers/TaskOpsInterestedUsers"),
    "TaskOpsInterestedUsers",
  ),
});

export const taskOpsParticipants = createRoute({
  getParentRoute: () => taskOps,
  path: "participants",
  validateSearch: taskOpsParticipantsSearch,
  loaderDeps: ({ search }) => search,
  loader: ({ deps, context }) => warmQueries(context.queryClient, taskOpsParticipantsQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/TaskOps/Participants"), "Participants"),
});

const taskOpsUploads = createRoute({
  getParentRoute: () => taskOps,
  path: "uploads",
  component: lazyRouteComponent(
    () => import("domains/admin/views/TaskTimeUpload/TaskTimeUploadContainer"),
    "TaskTimeUploadContainer",
  ),
});

export const invoicingRuns = createRoute({
  getParentRoute: () => adminRoot,
  path: "invoicing/runs",
  validateSearch: invoicingRunsSearch,
  loaderDeps: ({ search: { status, type } }) => ({ status, type }),
  loader: ({ deps, context }) => warmQueries(context.queryClient, invoicingRunsQueries({ search: deps })),
  component: lazyRouteComponent(() => import("domains/admin/views/InvoicingDrafts"), "Component"),
});

export const invoicingRunDetail = createRoute({
  getParentRoute: () => adminRoot,
  path: "invoicing/runs/$id",
  beforeLoad: ({ params }) => {
    const id = Number(params.id);
    if (!Number.isInteger(id) || id <= 0) throw redirect({ to: "/admin/invoicing/runs" });
  },
  loader: ({ params, context }) =>
    warmQueries(context.queryClient, invoicingRunDetailQueries({ runId: Number(params.id) })),
  component: lazyRouteComponent(() => import("domains/admin/views/InvoicingDraftDetail"), "Component"),
});

export const invoicingBackfills = createRoute({
  getParentRoute: () => adminRoot,
  path: "invoicing/backfills",
  loader: ({ context }) => warmQueries(context.queryClient, invoicingBackfillsQueries()),
  component: lazyRouteComponent(() => import("domains/admin/views/InvoicingBackfills"), "Component"),
});

/** The switch-on backfill's reconciliation report (CORE-8912). The API's Slack
 * summary links here; its `/invoicing/backfills/<id>` form (no `/admin`)
 * resolves through `ROUTE_ALIASES`. */
export const invoicingBackfillDetail = createRoute({
  getParentRoute: () => adminRoot,
  path: "invoicing/backfills/$id",
  beforeLoad: ({ params }) => {
    const id = Number(params.id);
    if (!Number.isInteger(id) || id <= 0) throw redirect({ to: "/admin/invoicing/backfills" });
  },
  loader: ({ params, context }) =>
    warmQueries(context.queryClient, invoicingBackfillDetailQueries({ backfillId: Number(params.id) })),
  component: lazyRouteComponent(() => import("domains/admin/views/InvoicingBackfillDetail"), "Component"),
});

export const adminRoutes = adminRoot.addChildren([
  index,
  pros,
  prosDetail.addChildren([
    prosDetailIndex,
    prosApplicationHistory,
    prosJobApplications,
    prosPerformance,
    prosSuspensions,
    prosBlocks,
    prosHighDemand,
    prosNotes,
    prosSickTime,
  ]),
  gigs,
  jobs,
  users,
  certifications,
  locations,
  commandCenter,
  atRiskPros,
  businessCoupons,
  dynamicLinks,
  referralCode,
  referralPromotions,
  configuration,
  permissions,
  adminAlerts,
  taskOps.addChildren([taskOpsIndex, taskOpsInterestedUsers, taskOpsParticipants, taskOpsUploads]),
  invoicingRuns,
  invoicingRunDetail,
  invoicingBackfills,
  invoicingBackfillDetail,
]);

const notificationSettings = createRoute({
  getParentRoute: () => signedUpRoute,
  path: "notification-settings",
  component: lazyRouteComponent(() => import("domains/admin/views/SmsNotificationSettings"), "SmsNotificationSettings"),
});

const settings = createRoute({
  getParentRoute: () => signedUpRoute,
  path: "settings",
  component: lazyRouteComponent(() => import("domains/settings/views/Root"), "SettingsRootView"),
});

export const settingsRoutes = [notificationSettings, settings];
